import { IImageProps } from 'telia-front-react';
import { makeAutoObservable } from 'mobx';
import ErrorUtil from '../error/ErrorUtil';
import { branchCloudinaryShopPicsUrl, getTranslationFallbackEt } from '../reservation/branchdetails/BranchUtil';
import API from '../util/ApiUtil';
import { FetchStatus } from '../util/FetchStatus';
import LanguageStore, { Language } from './LanguageStore';
import RootStore from './RootStore';

export type TranslationMap = {
  [lang in Language]?: string;
};

export interface SpecialHour {
  openTime?: string;
  closeTime?: string;
  closed: boolean;
  startDate: string;
  endDate: string;
}

export interface BusinessHour {
  openTime?: string;
  closeTime?: string;
  closed: boolean;
  startDay: string;
  endDay: string;
}

export interface BranchWithHours {
  branchId: string;
  name: string;
  googleMapsLink?: string;
  streetAddress?: TranslationMap;
  locationDescriptionLong?: TranslationMap;
  accessibilityInformation?: string[];
  specialHours?: SpecialHour[];
  businessHours?: BusinessHour[];
  arrivalWithPublicTransport?: TranslationMap;
  arrivalWithCar?: TranslationMap;
}

export interface BranchHolder {
  status: FetchStatus;
  branch: BranchWithHours;
  branchImages: IImageProps[];
}

export default class BranchStore {
  public branchHolderMap = new Map<string, BranchHolder>();

  private languageStore: LanguageStore;

  public constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  public fetchBranch = async (branchId: string): Promise<void> => {
    if (!branchId) {
      return;
    }
    this.setStatus(branchId, FetchStatus.FETCHING);
    try {
      const response = await API.get(`/reservation/branches/${branchId}`);
      this.setBranchState(branchId, response.data);
      if (this.branchHasMinimalInfo(branchId)) {
        this.loadBranchImages(branchId);
      }
      this.setStatus(branchId, FetchStatus.FETCHED);
    } catch (e: any) {
      this.setStatus(branchId, FetchStatus.ERROR);
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
    }
  };

  private branchHasMinimalInfo(branchId: string): boolean {
    const branch = this.getBranchState(branchId).branch;
    return !!getTranslationFallbackEt(branch?.locationDescriptionLong, this.languageStore.currentLanguage) && !!branch.accessibilityInformation?.length;
  }

  private async loadBranchImages(branchId: string): Promise<void> {
    const maxImages = 10;
    let fileExists = true;
    let fileCount = 1;
    const branch = this.getBranchState(branchId).branch;
    do {
      const url = `${branchCloudinaryShopPicsUrl}${branch.branchId}/${fileCount}.jpg`;
      try {
        await API.get('', { baseURL: url, withCredentials: false });
        this.addImages(branchId, { srcset: url, role: `presentation`, width: 400 });
        fileCount++;
      } catch {
        fileExists = false;
      }
    } while (fileExists && fileCount <= maxImages);
  }

  private addImages(branchId: string, imageProps: IImageProps): void {
    this.getBranchState(branchId).branchImages.push(imageProps);
  }

  public getBranchState(branchId: string) {
    if (!this.branchHolderMap.has(branchId)) {
      this.branchHolderMap.set(branchId, this.createEmptyState());
    }
    return this.branchHolderMap.get(branchId);
  }

  private setStatus(branchId: string, status: FetchStatus): void {
    this.getBranchState(branchId).status = status;
  }

  private setBranchState(branchId: string, branch: BranchWithHours): void {
    this.getBranchState(branchId).branch = branch;
  }

  private createEmptyState = (): BranchHolder => {
    return {
      branch: null,
      status: FetchStatus.UNINITIALIZED,
      branchImages: []
    };
  };
}
